"use client";

import { useFocusable } from "./OE2EFRVA.js";
import { createElement, createHook, forwardRef } from "./VOQWLFSQ.js";
import { useEvent, useMergeRefs, useMetadataProps } from "./5GGHRIN3.js";
import { __objRest, __spreadProps, __spreadValues } from "./3YLGPPWQ.js";

// src/command/command.tsx
import { isButton, isTextField } from "@ariakit/core/utils/dom";
import { fireClickEvent, isSelfTarget, queueBeforeEvent } from "@ariakit/core/utils/events";
import { disabledFromProps } from "@ariakit/core/utils/misc";
import { isFirefox } from "@ariakit/core/utils/platform";
import { useEffect, useRef, useState } from "react";
var TagName = "button";
function isNativeClick(event) {
  if (!event.isTrusted) return false;
  const element = event.currentTarget;
  if (event.key === "Enter") {
    return isButton(element) || element.tagName === "SUMMARY" || element.tagName === "A";
  }
  if (event.key === " ") {
    return isButton(element) || element.tagName === "SUMMARY" || element.tagName === "INPUT" || element.tagName === "SELECT";
  }
  return false;
}
var symbol = Symbol("command");
var useCommand = createHook(function useCommand2(_a) {
  var _b = _a,
    {
      clickOnEnter = true,
      clickOnSpace = true
    } = _b,
    props = __objRest(_b, ["clickOnEnter", "clickOnSpace"]);
  const ref = useRef(null);
  const [isNativeButton, setIsNativeButton] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    setIsNativeButton(isButton(ref.current));
  }, []);
  const [active, setActive] = useState(false);
  const activeRef = useRef(false);
  const disabled = disabledFromProps(props);
  const [isDuplicate, metadataProps] = useMetadataProps(props, symbol, true);
  const onKeyDownProp = props.onKeyDown;
  const onKeyDown = useEvent(event => {
    onKeyDownProp == null ? void 0 : onKeyDownProp(event);
    const element = event.currentTarget;
    if (event.defaultPrevented) return;
    if (isDuplicate) return;
    if (disabled) return;
    if (!isSelfTarget(event)) return;
    if (isTextField(element)) return;
    if (element.isContentEditable) return;
    const isEnter = clickOnEnter && event.key === "Enter";
    const isSpace = clickOnSpace && event.key === " ";
    const shouldPreventEnter = event.key === "Enter" && !clickOnEnter;
    const shouldPreventSpace = event.key === " " && !clickOnSpace;
    if (shouldPreventEnter || shouldPreventSpace) {
      event.preventDefault();
      return;
    }
    if (isEnter || isSpace) {
      const nativeClick = isNativeClick(event);
      if (isEnter) {
        if (!nativeClick) {
          event.preventDefault();
          const _a2 = event,
            {
              view
            } = _a2,
            eventInit = __objRest(_a2, ["view"]);
          const click = () => fireClickEvent(element, eventInit);
          if (isFirefox()) {
            queueBeforeEvent(element, "keyup", click);
          } else {
            queueMicrotask(click);
          }
        }
      } else if (isSpace) {
        activeRef.current = true;
        if (!nativeClick) {
          event.preventDefault();
          setActive(true);
        }
      }
    }
  });
  const onKeyUpProp = props.onKeyUp;
  const onKeyUp = useEvent(event => {
    onKeyUpProp == null ? void 0 : onKeyUpProp(event);
    if (event.defaultPrevented) return;
    if (isDuplicate) return;
    if (disabled) return;
    if (event.metaKey) return;
    const isSpace = clickOnSpace && event.key === " ";
    if (activeRef.current && isSpace) {
      activeRef.current = false;
      if (!isNativeClick(event)) {
        event.preventDefault();
        setActive(false);
        const element = event.currentTarget;
        const _a2 = event,
          {
            view
          } = _a2,
          eventInit = __objRest(_a2, ["view"]);
        queueMicrotask(() => fireClickEvent(element, eventInit));
      }
    }
  });
  props = __spreadProps(__spreadValues(__spreadValues({
    "data-active": active || void 0,
    type: isNativeButton ? "button" : void 0
  }, metadataProps), props), {
    ref: useMergeRefs(ref, props.ref),
    onKeyDown,
    onKeyUp
  });
  props = useFocusable(props);
  return props;
});
var Command = forwardRef(function Command2(props) {
  const htmlProps = useCommand(props);
  return createElement(TagName, htmlProps);
});
export { useCommand, Command };